import React, { useState } from 'react'
import { useExercise } from '../ExerciseProvider'
import { ClearSelectionsButton } from '~/components/shared/Buttons'
import { DndContext } from '@dnd-kit/core'
import { Draggable, Droppable } from '~/components/shared/DragAndDrop'
import { cn } from '~/lib/utils'
import { useVitalityExercise } from './VitalityExerciseProvider'

const VitalityExercisePageOne = () => {
  const { updateResponse, exerciseIndex } = useExercise()
  const {
    rankedPoints,
    setRankedPoints,
    pointsOfVitality,
    pageOneTranslations,
  } = useVitalityExercise()
  const handleClearSelections = () => {
    const newPoints = pointsOfVitality.map((point) => ({
      ...point,
      rank: null,
    }))
    setRankedPoints(newPoints)
    updateResponse(exerciseIndex, 'rankedPoints', newPoints)
  }

  const rankedPointsProps = {
    pageOneTranslations,
    rankedPoints,
    setRankedPoints,
  }

  return (
    <div className="flex flex-col gap-8">
      <RankPoints {...rankedPointsProps} />
      <div className="flex justify-end">
        <ClearSelectionsButton onClick={handleClearSelections}>
          {pageOneTranslations.clear_selections}
        </ClearSelectionsButton>
      </div>
    </div>
  )
}

export default VitalityExercisePageOne

const RankPoints = ({ pageOneTranslations, rankedPoints, setRankedPoints }) => {
  const { updateResponse, exerciseIndex, setContinueButtonDisabled } =
    useExercise()
  const pointsOfVitality = pageOneTranslations.points_of_vitality
  const [dragging, setDragging] = useState(false)

  const unrankedItems = rankedPoints.filter((g) => g.rank === null)

  const getRankedItem = (rank) => {
    const rankedItem = rankedPoints.find((i) => parseInt(i.rank) === rank)
    return rankedItem ? rankedItem : null
  }

  const getRank = (slug) => {
    const rankedItem = rankedPoints.find((g) => g.slug === slug)
    return rankedItem && rankedItem.rank
  }

  const updateRanking = (slug, rank) => {
    let rankToCompare = rank
    const newRankedItems = rankedPoints.map((i) => {
      if (i.slug === slug) {
        return { ...i, rank }
      }
      if (i.rank === rankToCompare) {
        if (i.rank >= pointsOfVitality.length) {
          return { ...i, rank: null }
        }
        rankToCompare += 1
        return { ...i, rank: i.rank + 1 }
      }
      return i
    })
    setRankedPoints(newRankedItems)
    updateResponse(exerciseIndex, 'rankedPoints', newRankedItems)
    setContinueButtonDisabled(newRankedItems.some((g) => g.rank === null))
  }

  const removeRanking = (slug) => {
    const newRankedItems = rankedPoints.map((i) => {
      if (i.slug === slug) {
        return { ...i, rank: null }
      }
      return i
    })
    setRankedPoints(newRankedItems)
    updateResponse(exerciseIndex, 'rankedPoints', newRankedItems)
  }

  const handleDragStart = (e) => {
    const { active } = e
    const draggedSlug = active.id
    const rank = getRank(draggedSlug)
    if (rank) {
      setDragging(true)
    }
  }

  const handleDragEnd = (e) => {
    setDragging(false)
    const { active, over } = e
    if (active?.id && over?.id) {
      const draggedSlug = active.id
      const rank = over.id
      updateRanking(draggedSlug, rank)
    } else if (!over) {
      const draggedSlug = active.id
      removeRanking(draggedSlug, null)
    }
  }

  return (
    <div>
      <DndContext
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        autoScroll={false}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="order-last flex flex-col gap-4 md:order-first">
            {unrankedItems.map((point) => (
              <DraggableItem key={`draggable-${point.slug}`} id={point.slug}>
                {point.name}
              </DraggableItem>
            ))}
          </div>
          <div className="order-first flex flex-col gap-4 md:order-last">
            {pointsOfVitality.map((point, idx) => (
              <DroppableItem
                key={`droppable-container-${idx + 1}-${point}`}
                rankNumber={idx + 1}
                getRankedItem={getRankedItem}
                dragging={dragging}
              >
                <div className="flex items-center justify-center font-roboto">
                  {idx + 1 === 1 &&
                    `${pageOneTranslations.most_needs_attention} `}
                  {idx + 1 === pointsOfVitality.length &&
                    `${pageOneTranslations.most_in_place} `}
                </div>
              </DroppableItem>
            ))}
          </div>
        </div>
      </DndContext>
    </div>
  )
}

const DroppableItem = ({ rankNumber, getRankedItem, dragging, children }) => {
  const droppableClasses = cn(
    'flex h-10 justify-center rounded-[15px] border border-dashed border-intoo-blue-medium text-intoo-blue-medium',
  )

  const rankedItem = getRankedItem(rankNumber)
  if (rankedItem)
    return (
      <Droppable
        key={`ranked-draggable-${rankNumber}`}
        id={rankNumber}
        className={cn(dragging ? droppableClasses : 'w-full')}
      >
        <DraggableItem
          id={rankedItem.slug}
          className={'w-full bg-intoo-blue-medium text-white'}
        >
          {rankedItem.name}
        </DraggableItem>
      </Droppable>
    )
  return (
    <Droppable className={droppableClasses} id={rankNumber}>
      {children}
    </Droppable>
  )
}

const DraggableItem = ({ className, id, children }) => {
  return (
    <Draggable
      className={cn(
        'rounded-[15px] border-2 border-intoo-blue-medium bg-white font-roboto capitalize text-intoo-blue-medium hover:bg-intoo-blue-medium hover:text-white',
        className,
      )}
      id={id}
    >
      {children}
    </Draggable>
  )
}
